import React from 'react';
import { TrendingUp, Moon } from 'lucide-react';

const ContextImpact = ({ formData, updateFormData, t }) => {
  const triggers = [
    { value: 'noise', label: 'Bruit', icon: '🔊' },
    { value: 'stress', label: 'Stress', icon: '😰' },
    { value: 'fatigue', label: 'Fatigue', icon: '😴' },
    { value: 'caffeine', label: 'Caféine', icon: '☕' },
    { value: 'silence', label: 'Silence', icon: '🤫' },
    { value: 'other', label: 'Autre', icon: '➕' },
  ];

  const worstMoments = [
    { value: 'night', label: 'Cocoon', icon: '🌙' },
    { value: 'work', label: 'Travail', icon: '💼' },
    { value: 'reading', label: 'Lecture', icon: '📖' },
    { value: 'post_noise', label: 'Après bruit', icon: '🔊' },
    { value: 'other', label: 'Autre', icon: '➕' },
  ];

  const toggleItem = (field, value) => {
    const current = formData[field] || [];
    if (current.includes(value)) {
      updateFormData({ [field]: current.filter(v => v !== value) });
    } else {
      updateFormData({ [field]: [...current, value] });
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="inline-block p-3 bg-purple-100 rounded-2xl">
          <TrendingUp size={32} className="text-purple-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">
          Contexte & Impact
        </h2>
        <p className="text-gray-600">
          Comprenons ensemble ce qui influence vos acouphènes
        </p>
      </div>

      {/* Triggers */}
      <div className="space-y-3">
        <label className="text-sm font-semibold text-gray-900">
          Qu'est-ce qui déclenche ou aggrave vos acouphènes ?
        </label>
        <div className="grid grid-cols-3 gap-2">
          {triggers.map((trigger) => (
            <button
              key={trigger.value}
              onClick={() => toggleItem('triggers', trigger.value)}
              className={`p-3 rounded-xl font-medium text-sm transition-all ${
                (formData.triggers || []).includes(trigger.value)
                  ? 'bg-purple-500 text-white shadow-md'
                  : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-purple-300'
              }`}
            >
              <div className="text-xl mb-1">{trigger.icon}</div>
              <div className="text-xs">{trigger.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Worst Moments */}
      <div className="space-y-3">
        <label className="text-sm font-semibold text-gray-900">
          Quand sont-ils le plus gênants ?
        </label>
        <div className="grid grid-cols-3 gap-2">
          {worstMoments.map((moment) => (
            <button
              key={moment.value}
              onClick={() => toggleItem('worst_moments', moment.value)}
              className={`p-3 rounded-xl font-medium text-sm transition-all ${
                (formData.worst_moments || []).includes(moment.value)
                  ? 'bg-purple-500 text-white shadow-md'
                  : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-purple-300'
              }`}
            >
              <div className="text-xl mb-1">{moment.icon}</div>
              <div className="text-xs">{moment.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Distress Scale - Apple-style Slider */}
      <div className="space-y-4 bg-gradient-to-br from-orange-50 to-red-50 rounded-3xl p-6">
        <label className="text-sm font-semibold text-gray-900 flex items-center justify-between">
          <span>Niveau de gêne au quotidien</span>
          <span className="text-2xl font-bold text-orange-600">{formData.distress_0_10}</span>
        </label>
        
        <div className="space-y-2">
          <input
            type="range"
            min="0"
            max="10"
            value={formData.distress_0_10 || 5}
            onChange={(e) => updateFormData({ distress_0_10: parseInt(e.target.value) })}
            className="w-full h-3 bg-gradient-to-r from-green-400 via-yellow-400 to-red-500 rounded-full appearance-none cursor-pointer slider-thumb"
            style={{
              WebkitAppearance: 'none',
            }}
          />
          <div className="flex justify-between text-xs text-gray-600">
            <span>0 - Aucune gêne</span>
            <span>10 - Très gênant</span>
          </div>
        </div>
      </div>

      {/* Sleep Scale - Apple-style Slider */}
      <div className="space-y-4 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-3xl p-6">
        <label className="text-sm font-semibold text-gray-900 flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Moon size={18} className="text-blue-600" />
            Qualité du sommeil
          </span>
          <span className="text-2xl font-bold text-blue-600">{formData.sleep_0_10}</span>
        </label>
        
        <div className="space-y-2">
          <input
            type="range"
            min="0"
            max="10"
            value={formData.sleep_0_10 || 5}
            onChange={(e) => updateFormData({ sleep_0_10: parseInt(e.target.value) })}
            className="w-full h-3 bg-gradient-to-r from-red-500 via-yellow-400 to-green-400 rounded-full appearance-none cursor-pointer slider-thumb"
            style={{
              WebkitAppearance: 'none',
            }}
          />
          <div className="flex justify-between text-xs text-gray-600">
            <span>0 - Très mauvais</span>
            <span>10 - Excellent</span>
          </div>
        </div>
      </div>

      {/* CSS for slider thumb */}
      <style>{`
        .slider-thumb::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          border: 2px solid #3b82f6;
        }
        
        .slider-thumb::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          border: 2px solid #3b82f6;
        }
      `}</style>
    </div>
  );
};

export default ContextImpact;
